import {
  ChamberMemberProAccountAllocationTypeEnum,
  CmpReportTypeEnum,
} from "../../../graphql/types";

export type ReportFilterKey =
  | "date"
  | "membershipType"
  | "membershipTier"
  | "memberSize"
  | "membershipCategory"
  | "eventCode"
  | "eventName"
  | "eventType"
  | "sponsorshipLevel"
  | "ticketType"
  | "advertisingCode"
  | "advertisingOptions"
  | "leadCapture"
  | "createdBy"
  | "salesAccount";

export type ReportDefinition = {
  id: string;
  number: number;
  title: string;
  reportType: CmpReportTypeEnum;
  filters: readonly ReportFilterKey[];
  salesAccountType?: ChamberMemberProAccountAllocationTypeEnum;
};

const membershipFilters = [
  "date",
  "membershipType",
  "memberSize",
  "membershipCategory",
  "salesAccount",
] as const;

export const REPORT_DEFINITIONS: readonly ReportDefinition[] = [
  {
    id: "new-member",
    number: 1,
    title: "New Member Reports",
    reportType: CmpReportTypeEnum.NewMember,
    filters: membershipFilters,
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.MembershipSales,
  },
  {
    id: "suspended-member",
    number: 2,
    title: "Suspended Member Reports",
    reportType: CmpReportTypeEnum.SuspendedMember,
    filters: membershipFilters,
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.MembershipSales,
  },
  {
    id: "cancelled-member",
    number: 3,
    title: "Cancelled Member Reports",
    reportType: CmpReportTypeEnum.CancelledMember,
    filters: membershipFilters,
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.MembershipSales,
  },
  {
    id: "member-renewal",
    number: 4,
    title: "Member Renewal Reports",
    reportType: CmpReportTypeEnum.MemberRenewal,
    filters: membershipFilters,
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.MembershipSales,
  },
  {
    id: "membership-accounts-receivable",
    number: 5,
    title: "Membership Accounts Receivable",
    reportType: CmpReportTypeEnum.MembershipAccountsReceivable,
    filters: membershipFilters,
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.MembershipSales,
  },
  {
    id: "member-chamber-anniversary",
    number: 6,
    title: "Member Chamber Anniversary Report",
    reportType: CmpReportTypeEnum.MemberChamberAnniversary,
    filters: membershipFilters,
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.MembershipSales,
  },
  {
    id: "member-years-in-business",
    number: 7,
    title: "Member Years in Business Report",
    reportType: CmpReportTypeEnum.MemberYearsInBusiness,
    filters: membershipFilters,
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.MembershipSales,
  },
  {
    id: "members-by-category",
    number: 8,
    title: "Members by Category Report",
    reportType: CmpReportTypeEnum.MembersByCategory,
    filters: [
      "date",
      "membershipType",
      "memberSize",
      "salesAccount",
    ],
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.MembershipSales,
  },
  {
    id: "members-by-employee-size",
    number: 9,
    title: "Members by Type (Employee Size Range) Report",
    reportType: CmpReportTypeEnum.MembersByEmployeeSize,
    filters: membershipFilters,
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.MembershipSales,
  },
  {
    id: "members-by-tier",
    number: 10,
    title: "Members by Tiers Report",
    reportType: CmpReportTypeEnum.MembersByTier,
    filters: [
      "date",
      "membershipTier",
      "memberSize",
      "membershipCategory",
      "salesAccount",
    ],
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.MembershipSales,
  },
  {
    id: "chamber-group-insurance",
    number: 11,
    title: "Members Belonging to Chamber Group Insurance Plan Report",
    reportType: CmpReportTypeEnum.ChamberGroupInsurance,
    filters: membershipFilters,
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.MembershipSales,
  },
  {
    id: "member-dashboard-sign-ins",
    number: 12,
    title: "Members Signed into the Member Dashboard Report",
    reportType: CmpReportTypeEnum.MemberDashboardSignIns,
    filters: [
      "date",
      "membershipType",
      "memberSize",
      "membershipCategory",
    ],
  },
  {
    id: "monthly-payment-members",
    number: 13,
    title: "Members on Monthly Payment Reports",
    reportType: CmpReportTypeEnum.MonthlyPaymentMembers,
    filters: membershipFilters,
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.MembershipSales,
  },
  {
    id: "auto-renewal-members",
    number: 14,
    title: "Members on Auto-Renewal Report",
    reportType: CmpReportTypeEnum.AutoRenewalMembers,
    filters: membershipFilters,
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.MembershipSales,
  },
  {
    id: "event-revenue",
    number: 15,
    title: "Event Revenue Report",
    reportType: CmpReportTypeEnum.EventRevenue,
    filters: ["eventCode", "eventName", "eventType", "salesAccount"],
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.EventTicketSales,
  },
  {
    id: "event-sponsorship-revenue",
    number: 16,
    title: "Event Sponsorship Revenue Report",
    reportType: CmpReportTypeEnum.EventSponsorshipRevenue,
    filters: [
      "eventCode",
      "eventName",
      "eventType",
      "sponsorshipLevel",
      "salesAccount",
    ],
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.SponsorshipSales,
  },
  {
    id: "event-ticket-revenue",
    number: 17,
    title: "Event Ticket Revenue Report",
    reportType: CmpReportTypeEnum.EventTicketRevenue,
    filters: [
      "eventCode",
      "eventName",
      "eventType",
      "ticketType",
      "salesAccount",
    ],
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.EventTicketSales,
  },
  {
    id: "advertising-revenue",
    number: 18,
    title: "Advertising Revenue Report",
    reportType: CmpReportTypeEnum.AdvertisingRevenue,
    filters: ["advertisingOptions", "advertisingCode", "salesAccount"],
    salesAccountType:
      ChamberMemberProAccountAllocationTypeEnum.AdvertisingSales,
  },
  {
    id: "custom-revenue",
    number: 19,
    title: "Custom Revenue Report",
    reportType: CmpReportTypeEnum.CustomRevenue,
    filters: ["date", "salesAccount"],
    salesAccountType: ChamberMemberProAccountAllocationTypeEnum.CustomSales,
  },
  {
    id: "prospect",
    number: 20,
    title: "Prospect Report",
    reportType: CmpReportTypeEnum.Prospect,
    filters: ["date", "leadCapture", "createdBy"],
  },
];

export const reportHasFilter = ({
  report,
  filter,
}: {
  report: ReportDefinition;
  filter: ReportFilterKey;
}) => report.filters.includes(filter);
